import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { usePageSize } from '~settings/hooks/usePageSize';

// These keys are excluded from the difference calculation
// Example keys could be page or any other param that is not relevant for the table data
const EXCLUDE_PARAMS = ['page'];

/**
 * Calculate the difference between two URLSearchParams objects.
 * It returns a new URLSearchParams object containing the parameters that are
 * present in the newSearchParams but not in the oldSearchParams.
 *
 * It ignores the 'page', 'sort', and 'order' values.
 *
 * @param oldSearchParams
 * @param newSearchParams
 * @returns
 */
const differenceSearchParams = (
  oldSearchParams: URLSearchParams | undefined,
  newSearchParams: URLSearchParams,
) => {
  const res = new URLSearchParams();

  for (const [key, value] of newSearchParams) {
    if (EXCLUDE_PARAMS.includes(key)) {
      continue;
    }

    if (oldSearchParams?.get(key) !== value) {
      res.append(key, value);
    }
  }

  return res;
};

export const usePagination = (perPage?: number) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const oldSearchParams = useRef<typeof searchParams>(searchParams);
  const pageParam = searchParams.get('page');
  const pageSize = usePageSize(perPage);

  // Search params is the local state storage for the page number
  const pageNumber =
    pageParam === null || Number.isNaN(Number(pageParam)) || +pageParam < 1
      ? 1
      : +pageParam;

  const setPageNumber = useCallback(
    (newPage: number) => {
      const params = new URLSearchParams(window.location.search);

      if (newPage > 1) {
        params.set('page', `${newPage}`);
      } else {
        params.delete('page');
      }

      setSearchParams(params);
    },
    [setSearchParams],
  );

  // On search params change, if the new search params are different from the
  // old search params, reset the page number to 1
  useEffect(() => {
    const newSearchParams = differenceSearchParams(
      oldSearchParams.current,
      searchParams,
    );

    // Removing sort should trigger a reset of the page number as it changes the order
    const hasRemovedSort =
      oldSearchParams.current.has('sortBy') && !searchParams.has('sortBy');

    if (newSearchParams.toString() !== '' || hasRemovedSort) {
      setPageNumber(1);
    }

    oldSearchParams.current = searchParams;
  }, [searchParams, setPageNumber]);

  const skip = (pageNumber - 1) * pageSize;
  const take = pageSize;

  return {
    page: pageNumber,
    setPageNumber,
    perPage: pageSize,
    skip,
    take,
  };
};

export const usePaginationMessage = (
  page: number,
  perPage: number,
  total: number,
  entity?: string,
) => {
  const { t } = useTranslation('settings');

  const translatedEntity = entity ? t(entity) : '';

  const start = useMemo(() => (page - 1) * perPage + 1, [page, perPage]);
  const end = useMemo(
    () => Math.min(page * perPage, total),
    [page, perPage, total],
  );

  const message = t('pagination.label', {
    start,
    end,
    total: total.toLocaleString(),
    entity: translatedEntity.toLocaleLowerCase(),
  });

  return message;
};
